import type BrowserNotificationInput from '@/features/push/types/browser-notification-input';
import type FcmNotificationPayload from '@/features/push/types/fcm-notification-payload';

/**
 * Shows a browser notification from a page (foreground) context.
 * Requires existing Notification permission; does not request it.
 */
export function showBrowserNotification(input: BrowserNotificationInput): Notification | null {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    Notification.permission !== 'granted'
  )
    return null;
  // eslint-disable-next-line compat/compat
  return new Notification(input.title, toNotificationOptions(input));
}

/**
 * Converts a BrowserNotificationInput to a NotificationOptions.
 * @param input
 * @returns
 */
export function toNotificationOptions(input: BrowserNotificationInput): NotificationOptions {
  return {
    body: input.body,
    icon: input.icon,
    ...(input.image ? { image: input.image } : {}),
    tag: input.tag,
    data: input.data ?? {},
  };
}

/**
 * Builds a BrowserNotificationInput from a FcmNotificationPayload.
 *
 * @param payload
 * @param fallbackTitle
 * @returns
 */
export function buildBrowserNotificationInput(
  payload: FcmNotificationPayload,
  fallbackTitle = 'Notification'
): BrowserNotificationInput {
  return {
    title: payload.notification?.title ?? fallbackTitle,
    body: payload.notification?.body,
    icon: payload.notification?.icon,
    image: payload.notification?.image,
    data: payload.data ?? {},
  };
}

/**
 * Shows a browser notification from a FcmNotificationPayload.
 * @param payload
 * @param fallbackTitle
 * @returns
 */
export function showBrowserNotificationFromFcmPayload(
  payload: FcmNotificationPayload,
  fallbackTitle = 'Notification'
): Notification | null {
  return showBrowserNotification(buildBrowserNotificationInput(payload, fallbackTitle));
}
